import { Box, Button } from "@mui/material";
import { Popup } from "devextreme-react/ui/popup";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { useEffect, useRef, useState } from "react";
import Loader from "../../components/Loader";
import ToolBarItems from "../../components/ToolBarItems";
import { usePermissions } from "../../hooks/usePermissions";
import styled from "styled-components";
import { CustomField } from "@/Features";
import EvidenceTypesAPI from "@/api/evidence-types";
import { useTheme } from "@monolith-forensics/monolith-ui";

interface EvidenceType {
  custom_fields?: CustomField[];
  evi_type: string;
  evi_type_id: number;
  total: number;
}

const CaseTypesList = styled(
  ({
    className,
    evidenceTypes,
    setEvidenceTypes,
    setDeleteSelection,
    deleteEvidenceTypePopup,
  }) => {
    useEffect(() => {
      EvidenceTypesAPI.getEvidenceTypes().then((result) => {
        setEvidenceTypes(result);
      });
    }, []);

    return (
      <div className={className}>
        {!evidenceTypes && <Loader />}
        {evidenceTypes &&
          evidenceTypes.map((type: EvidenceType) => {
            return (
              <CaseTypeItem
                key={type.evi_type_id}
                data={type}
                evidenceTypes={evidenceTypes}
                setEvidenceTypes={setEvidenceTypes}
                setDeleteSelection={setDeleteSelection}
                deleteEvidenceTypePopup={deleteEvidenceTypePopup}
              />
            );
          })}
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const CaseTypeItem = ({
  data,
  evidenceTypes,
  setEvidenceTypes,
  setDeleteSelection,
  deleteEvidenceTypePopup,
}: {
  data: EvidenceType;
  evidenceTypes: EvidenceType[];
  setEvidenceTypes: React.Dispatch<React.SetStateAction<EvidenceType[] | null>>;
  setDeleteSelection: React.Dispatch<React.SetStateAction<EvidenceType | null>>;
  deleteEvidenceTypePopup: React.MutableRefObject<any>;
}) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const handleTypeUpdate = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.innerText === "") {
      event.currentTarget.innerText = data.evi_type;
      return;
    }
    if (event.currentTarget.innerText !== data.evi_type) {
      EvidenceTypesAPI.updateEvidenceType(data.evi_type_id, {
        evi_type: event.currentTarget.innerText,
      });
      setEvidenceTypes(
        evidenceTypes.map((type) => {
          if (type.evi_type_id === data.evi_type_id) {
            type.evi_type = event.currentTarget.innerText;
          }
          return type;
        })
      );
    }
  };

  const handleDelete = () => {
    setDeleteSelection(data);
    deleteEvidenceTypePopup.current.instance.show();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "10px 10px",
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <div>
        <Box
          contentEditable={true}
          suppressContentEditableWarning
          sx={{
            fontSize: "larger",
            padding: "2px",
            cursor: "text",
            "&:hover": { outline: `1px solid slategray` },
            "&:focus": { outline: `1px solid slategray` },
          }}
          onBlur={handleTypeUpdate}
        >
          {data.evi_type}
        </Box>
        <div style={{ color: "slategrey", padding: "2px" }}>
          {data.total} Evidence Items
        </div>
      </div>
      <div style={{ marginLeft: "auto" }}>
        <Button
          variant="text"
          color="error"
          size="small"
          disabled={!hasPermission()}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </div>
    </Box>
  );
};

const CreateCaseTypePopup = ({
  createEvidenceTypePopup,
  evidenceTypes,
  setEvidenceTypes,
}: {
  createEvidenceTypePopup: React.MutableRefObject<any>;
  evidenceTypes: EvidenceType[] | null;
  setEvidenceTypes: React.Dispatch<React.SetStateAction<EvidenceType[] | null>>;
}) => {
  const form: any = useRef(null);

  return (
    <Popup
      ref={createEvidenceTypePopup}
      showTitle={true}
      showCloseButton={true}
      title="Create New Evidence Type"
      defaultHeight={185}
      defaultWidth={400}
      onHidden={(e) => {
        form.current.instance.resetValues();
      }}
    >
      <Form ref={form}>
        <SimpleItem
          dataField="evi_type"
          label={{ text: "Evidence Type" }}
          isRequired={true}
        ></SimpleItem>
      </Form>
      <ToolBarItems
        submitText="Create Evidence Type"
        onSubmit={() => {
          if (form.current.instance.validate().isValid) {
            let values = Object.assign(
              {},
              form.current.instance.option("formData")
            );

            EvidenceTypesAPI.createEvidenceType(values).then((result) => {
              setEvidenceTypes([
                ...(evidenceTypes || []),
                {
                  evi_type_id: result.evi_type_id,
                  evi_type: values.evi_type,
                  total: 0,
                },
              ]);
            });

            createEvidenceTypePopup.current.instance.hide();
          }
        }}
        onCancel={() => {
          createEvidenceTypePopup.current.instance.hide().then(() => {
            form.current.instance.resetValues();
          });
        }}
      />
    </Popup>
  );
};

const DeleteCaseTypePopup = ({
  deleteEvidenceTypePopup,
  evidenceTypes,
  setEvidenceTypes,
  deleteSelection,
}: {
  deleteEvidenceTypePopup: React.MutableRefObject<any>;
  evidenceTypes: EvidenceType[];
  setEvidenceTypes: React.Dispatch<React.SetStateAction<EvidenceType[] | null>>;
  deleteSelection: EvidenceType | null;
}) => {
  const [items, setItems] = useState(evidenceTypes);
  const form: any = useRef(null);

  useEffect(() => {
    deleteSelection &&
      setItems(
        evidenceTypes.filter(
          (item: EvidenceType) =>
            item.evi_type_id !== deleteSelection.evi_type_id
        )
      );
  }, [deleteSelection]);

  return (
    <Popup
      ref={deleteEvidenceTypePopup}
      showTitle={true}
      showCloseButton={true}
      title={`Delete Evidence Type ${deleteSelection?.evi_type || ""}`}
      defaultHeight={250}
      defaultWidth={450}
      onHidden={(e) => {
        if (form.current) form.current.instance.resetValues();
      }}
    >
      {deleteSelection?.total === 0 && (
        <div style={{ margin: "15px 0px", minHeight: 115 }}>
          Are you sure you want to delete this evidence type?
        </div>
      )}
      {deleteSelection?.total! > 0 && (
        <div style={{ minHeight: 115 }}>
          <div style={{ margin: "15px 0px" }}>
            Select the evidence type that should replace this one when deleted:
          </div>
          <Form ref={form}>
            <SimpleItem
              dataField="evi_type"
              label={{ text: "Evidence Type" }}
              isRequired={true}
              editorType="dxSelectBox"
              editorOptions={{
                items: items,
                displayExpr: "evi_type",
              }}
            ></SimpleItem>
          </Form>
        </div>
      )}
      <ToolBarItems
        submitText="Delete Evidence Type"
        submitColor="error"
        onSubmit={() => {
          if (form.current) {
            if (form.current.instance.validate().isValid) {
              let values = Object.assign(
                {},
                form.current.instance.option("formData")
              );
              if (deleteSelection?.evi_type_id) {
                EvidenceTypesAPI.deleteEvidenceType(
                  deleteSelection.evi_type_id,
                  {
                    replace: values.evi_type.evi_type,
                  }
                );
              }
              setEvidenceTypes(
                items.map((item) => {
                  if (item.evi_type_id === values.evi_type.evi_type_id) {
                    item.total += deleteSelection?.total!;
                  }
                  return item;
                })
              );
              deleteEvidenceTypePopup.current.instance.hide();
            }
          } else {
            deleteEvidenceTypePopup.current.instance.hide();
            if (deleteSelection?.evi_type_id) {
              EvidenceTypesAPI.deleteEvidenceType(deleteSelection?.evi_type_id);
            }
            setEvidenceTypes(items);
          }
        }}
        onCancel={() => {
          deleteEvidenceTypePopup.current.instance.hide().then(() => {
            if (form.current) form.current.instance.resetValues();
          });
        }}
      />
    </Popup>
  );
};

const EvidenceTypeSettings = styled(({ className }) => {
  const [evidenceTypes, setEvidenceTypes] = useState<EvidenceType[] | null>(
    null
  );
  const [deleteSelection, setDeleteSelection] = useState<EvidenceType | null>(
    null
  );
  const createEvidenceTypePopup = useRef<any>(null);
  const deleteEvidenceTypePopup = useRef<any>(null);

  return (
    <div className={className} style={{ maxWidth: 800, padding: "0px 30px" }}>
      <div>
        Create, delete, and edit evidence types. Create evidence types that will
        help categorize the forensic evidence collected and processed by your
        organization.
      </div>
      <div style={{ margin: "25px 0px" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={(e) => {
            createEvidenceTypePopup.current.instance.show();
          }}
        >
          + Create Evidence Type
        </Button>
      </div>
      <CaseTypesList
        evidenceTypes={evidenceTypes}
        setEvidenceTypes={setEvidenceTypes}
        setDeleteSelection={setDeleteSelection}
        deleteEvidenceTypePopup={deleteEvidenceTypePopup}
      />
      <CreateCaseTypePopup
        createEvidenceTypePopup={createEvidenceTypePopup}
        evidenceTypes={evidenceTypes}
        setEvidenceTypes={setEvidenceTypes}
      />
      {evidenceTypes && (
        <DeleteCaseTypePopup
          deleteEvidenceTypePopup={deleteEvidenceTypePopup}
          evidenceTypes={evidenceTypes}
          setEvidenceTypes={setEvidenceTypes}
          deleteSelection={deleteSelection}
        />
      )}
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;
`;

export default EvidenceTypeSettings;
